import { useEffect, useRef } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import {
  Activity,
  AlertTriangle,
  Archive,
  Bell,
  Bug,
  Cpu,
  Globe,
  Mail,
  Layers,
  LayoutDashboard,
  LineChart,
  LogIn,
  MessageSquare,
  Play,
  Send,
  Server,
  ShieldCheck,
  Sparkles,
  Wrench,
  Zap,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import type { Lang } from '@/lib/i18n'
import { cn } from '@/lib/utils'

const ADMIN_URL = '/login'

type Copy = {
  nav: { features: string; video: string; stack: string; admin: string }
  hero: {
    badge: string
    headline: [string, string, string]
    sub: string
    cta_admin: string
    cta_video: string
    proof_left: string
    proof_right: string
    chips: string[]
  }
  video: { kicker: string; title: string; caption: string }
  pillars: { title: string; sub: string; items: Array<{ icon: string; title: string; body: string }> }
  features: { title: string; sub: string; cards: Array<{ icon: string; title: string; body: string; tag?: string }> }
  alerting: {
    kicker: string
    title: string
    sub: string
    rules: Array<{ key: string; label: string; sev: 'critical' | 'warning' | 'info' }>
    channels: Array<{ icon: string; name: string; body: string }>
  }
  telegram: { kicker: string; title: string; bullets: string[]; replyA: string; replyB: string }
  backups: { kicker: string; title: string; bullets: string[] }
  stack: { title: string; sub: string; groups: Array<{ title: string; items: string[] }> }
  cta: { title: string; sub: string; button: string }
  footer: { tagline: string; built: string }
}

const COPY: Record<Lang, Copy> = {
  en: {
    nav: { features: 'Features', video: 'Video', stack: 'Stack', admin: 'Open admin' },
    hero: {
      badge: 'v1.0 — six milestones shipped',
      headline: ['One platform for', 'Docker Swarm', 'SwarmOps'],
      sub: 'Zeus unifies monitoring, alerting, log analysis and backups for your Docker Swarm — with first-class GPU attribution, an interactive Telegram bot, and online Postgres + Neo4j dumps. Built by Sponge Theory, runs on your own iron.',
      cta_admin: 'Open admin',
      cta_video: 'Watch the 80-second tour',
      proof_left: 'Self-hosted',
      proof_right: 'Single mono-image, role-based CLI',
      chips: ['Docker Swarm native', 'TimescaleDB hypertables', '16 built-in alert rules', 'GPU → container attribution', '2-way Telegram bot', 'APOC online Neo4j dumps'],
    },
    video: {
      kicker: 'See it in 80 seconds',
      title: 'A guided tour of the platform',
      caption: 'Discovery, live metrics, GPU attribution, alerting, Telegram acks, log findings, backups — the full stack in one take.',
    },
    pillars: {
      title: 'Six pillars, one binary',
      sub: 'A single mono-image runs each role: api, scheduler, collector, log-scanner, backup-worker.',
      items: [
        { icon: 'Layers', title: 'Discovery', body: 'Auto-discovers nodes, stacks, services, containers — classifier auto-tags databases and web.' },
        { icon: 'LineChart', title: 'Metrics', body: 'CPU, RAM, disk, network, GPU per container, Mac thermal pressure — stored in TimescaleDB hypertables.' },
        { icon: 'Activity', title: 'Health', body: 'HTTP probes with CRUD, history, immediate retry, auto-disable on flapping.' },
        { icon: 'Bell', title: 'Alerting', body: '16 built-in rules, three notification channels, silences and maintenance windows.' },
        { icon: 'Bug', title: 'Log findings', body: 'Container + journald scanner. NVRM OOM, systemd memory pressure, kernel events.' },
        { icon: 'Archive', title: 'Backups', body: 'Postgres + Neo4j online dumps, encrypted at rest, GFS retention, S3 optional.' },
      ],
    },
    features: {
      title: 'What makes Zeus different',
      sub: 'Not just dashboards — operational primitives a single SRE can actually use.',
      cards: [
        { icon: 'Cpu', title: 'GPU process → container attribution', body: 'Maps NVIDIA GPU processes back to the Docker container that owns them. See which workload is burning your VRAM, in real time.', tag: 'GPU' },
        { icon: 'MessageSquare', title: 'Interactive Telegram bot', body: 'Two-way — slash commands, inline buttons, confirmation flows, rate-limit, dedup, audit log. Acknowledge alerts from your phone.', tag: 'ChatOps' },
        { icon: 'ShieldCheck', title: 'One-time signed ack URLs', body: 'Every alert email and Telegram message embeds a tokenised one-click ack URL. No login, no impersonation.', tag: 'Security' },
        { icon: 'Archive', title: 'Online Neo4j dumps via APOC', body: 'Hot dumps of running Neo4j databases without downtime. Postgres via ephemeral pgdump containers.', tag: 'Backups' },
        { icon: 'Server', title: 'Mac agent for Apple Silicon', body: 'Native LaunchDaemon for M-series nodes — host, GPU and omlx telemetry, self-registers without Swarm membership.', tag: 'Mac' },
        { icon: 'AlertTriangle', title: 'Kernel-level signal', body: 'Tails journald and /var/log/journal for NVRM OOM and systemd memory pressure — catches GPU and memory crashes that container logs miss.', tag: 'Kernel' },
      ],
    },
    alerting: {
      kicker: 'Alerting',
      title: '16 rules, 3 channels, zero noise',
      sub: 'Fingerprint dedup, per-rule severity overrides, ad-hoc silences and planned maintenance windows.',
      rules: [
        { key: 'node_down', label: 'node_down', sev: 'critical' },
        { key: 'disk_critical', label: 'disk_critical', sev: 'critical' },
        { key: 'node_cpu_temp_high', label: 'node_cpu_temp_high', sev: 'warning' },
        { key: 'service_replicas_degraded', label: 'service_replicas_degraded', sev: 'warning' },
        { key: 'container_restart_loop', label: 'container_restart_loop', sev: 'warning' },
        { key: 'gpu_mem_exhausted', label: 'gpu_mem_exhausted', sev: 'critical' },
        { key: 'gpu_down', label: 'gpu_down', sev: 'critical' },
        { key: 'kernel_nvrm_oom', label: 'kernel_nvrm_oom', sev: 'warning' },
        { key: 'sys_memory_pressure', label: 'sys_memory_pressure', sev: 'warning' },
        { key: 'api_5xx', label: 'api_5xx', sev: 'warning' },
        { key: 'log_critical', label: 'log_critical', sev: 'critical' },
        { key: 'log_error_rate', label: 'log_error_rate', sev: 'warning' },
      ],
      channels: [
        { icon: 'Send', name: 'Email — Resend', body: 'HTML email with one-time ack URL embedded.' },
        { icon: 'MessageSquare', name: 'Telegram', body: 'Inline buttons. Ack from a tap.' },
        { icon: 'Wrench', name: 'Webhook', body: 'Generic JSON POST for your own pipeline.' },
      ],
    },
    telegram: {
      kicker: 'Telegram, two-way',
      title: 'Acknowledge alerts from a single tap',
      bullets: [
        'Slash commands — /status, /silence, /maintenance, /ack',
        'Inline buttons, confirmation flows, audit log',
        'Per-user binding (Telegram ↔ Zeus user), scoped to severity',
        'Rate-limited, deduplicated, fingerprinted',
      ],
      replyA: 'Acknowledged by christophe — 12 seconds ago',
      replyB: 'gpu_mem_exhausted on spt-node4 · GPU 0 · 23.8 GiB / 24 GiB',
    },
    backups: {
      kicker: 'Backups',
      title: 'Online Postgres + Neo4j, encrypted, retained',
      bullets: [
        'PostgreSQL dumps via ephemeral zeus-pgdump containers (aiodocker)',
        'Neo4j online dumps via APOC — no downtime',
        'Encrypted with zeus_backup_encryption_key',
        'GFS retention (daily, weekly, monthly), optional S3 upload',
        'pgdump-warmer service caches the image on the manager',
        'Ad-hoc /run-now endpoint for manual dumps',
      ],
    },
    stack: {
      title: 'Built with',
      sub: 'Modern Python, modern React, no surprises.',
      groups: [
        { title: 'Backend', items: ['Python 3.12', 'FastAPI', 'SQLAlchemy 2', 'asyncpg', 'Alembic', 'structlog', 'aiodocker', 'croniter', 'Argon2'] },
        { title: 'Data', items: ['PostgreSQL 16', 'TimescaleDB hypertables'] },
        { title: 'Frontend', items: ['React 18', 'Vite', 'TypeScript', 'shadcn/ui', 'Radix', 'Tailwind', 'TanStack Query', 'react-hook-form + zod', 'zustand', 'recharts'] },
        { title: 'Infrastructure', items: ['Docker Swarm', 'Mono-image, role-based CLI', 'uv (Python)', 'npm (UI)'] },
      ],
    },
    cta: {
      title: 'Your cluster, instrumented',
      sub: 'Self-host on the Swarm you already run. Single mono-image, one stack file, sign in with your admin key.',
      button: 'Open admin',
    },
    footer: { tagline: 'Zeus — Unified monitoring, alerting, log analysis & backup for Docker Swarm', built: 'Built by Sponge Theory' },
  },
  fr: {
    nav: { features: 'Fonctionnalités', video: 'Vidéo', stack: 'Stack', admin: "Accéder à l'admin" },
    hero: {
      badge: 'v1.0 — six jalons livrés',
      headline: ['Une plateforme pour', 'Docker Swarm', 'SwarmOps'],
      sub: "Zeus unifie monitoring, alerting, analyse de logs et sauvegardes pour votre Docker Swarm — avec attribution GPU par conteneur, bot Telegram interactif, et dumps Postgres + Neo4j à chaud. Conçu par Sponge Theory, tourne sur votre infra.",
      cta_admin: "Accéder à l'admin",
      cta_video: 'Voir la démo de 80 secondes',
      proof_left: 'Self-hosted',
      proof_right: 'Mono-image, CLI à rôles',
      chips: ['Natif Docker Swarm', 'Hypertables TimescaleDB', '16 règles d\'alerte', 'GPU → conteneur', 'Bot Telegram bidirectionnel', 'Dumps Neo4j à chaud (APOC)'],
    },
    video: {
      kicker: 'Voir Zeus en 80 secondes',
      title: 'Une visite guidée de la plateforme',
      caption: 'Découverte, métriques temps réel, attribution GPU, alerting, ack Telegram, findings de logs, backups — toute la stack en une seule prise.',
    },
    pillars: {
      title: 'Six piliers, un seul binaire',
      sub: 'Une seule image multi-rôles : api, scheduler, collector, log-scanner, backup-worker.',
      items: [
        { icon: 'Layers', title: 'Découverte', body: 'Auto-découvre nœuds, stacks, services, conteneurs — le classifier tague les bases et les services web automatiquement.' },
        { icon: 'LineChart', title: 'Métriques', body: 'CPU, RAM, disque, réseau, GPU par conteneur, pression thermique Mac — dans des hypertables TimescaleDB.' },
        { icon: 'Activity', title: 'Health checks', body: 'Sondes HTTP avec CRUD, historique, retry immédiat, auto-désactivation des flappers.' },
        { icon: 'Bell', title: 'Alerting', body: '16 règles intégrées, 3 canaux de notification, silences et fenêtres de maintenance.' },
        { icon: 'Bug', title: 'Findings logs', body: 'Scanner conteneurs + journald. NVRM OOM, pression mémoire systemd, événements kernel.' },
        { icon: 'Archive', title: 'Sauvegardes', body: 'Dumps Postgres + Neo4j à chaud, chiffrés au repos, rétention GFS, S3 optionnel.' },
      ],
    },
    features: {
      title: 'Ce qui rend Zeus différent',
      sub: 'Pas que des dashboards — des primitives opérationnelles utilisables par un SRE seul.',
      cards: [
        { icon: 'Cpu', title: 'Attribution GPU → conteneur', body: "Mappe les processus GPU NVIDIA au conteneur Docker qui les exécute. Voyez en temps réel quel workload brûle votre VRAM.", tag: 'GPU' },
        { icon: 'MessageSquare', title: 'Bot Telegram interactif', body: "Bidirectionnel — commandes slash, boutons inline, confirmations, rate-limit, déduplication, audit log. Acquittez les alertes depuis votre téléphone.", tag: 'ChatOps' },
        { icon: 'ShieldCheck', title: 'URLs d\'ack à usage unique', body: 'Chaque email d\'alerte et message Telegram embarque une URL d\'ack tokenisée à usage unique. Aucun login, aucune usurpation.', tag: 'Sécurité' },
        { icon: 'Archive', title: 'Dumps Neo4j à chaud (APOC)', body: 'Dumps à chaud de bases Neo4j en cours d\'exécution, sans coupure. Postgres via conteneurs pgdump éphémères.', tag: 'Backups' },
        { icon: 'Server', title: 'Agent macOS Apple Silicon', body: 'LaunchDaemon natif pour les nœuds M-series — host, GPU et télémetrie omlx, auto-enregistré sans appartenance Swarm.', tag: 'Mac' },
        { icon: 'AlertTriangle', title: 'Signal kernel', body: "Tail journald et /var/log/journal pour NVRM OOM et la pression mémoire systemd — capte les crashes GPU et mémoire que les logs conteneurs ratent.", tag: 'Kernel' },
      ],
    },
    alerting: {
      kicker: 'Alerting',
      title: '16 règles, 3 canaux, zéro bruit',
      sub: 'Déduplication par fingerprint, surcharges de sévérité par règle, silences ad-hoc et fenêtres de maintenance planifiées.',
      rules: [
        { key: 'node_down', label: 'node_down', sev: 'critical' },
        { key: 'disk_critical', label: 'disk_critical', sev: 'critical' },
        { key: 'node_cpu_temp_high', label: 'node_cpu_temp_high', sev: 'warning' },
        { key: 'service_replicas_degraded', label: 'service_replicas_degraded', sev: 'warning' },
        { key: 'container_restart_loop', label: 'container_restart_loop', sev: 'warning' },
        { key: 'gpu_mem_exhausted', label: 'gpu_mem_exhausted', sev: 'critical' },
        { key: 'gpu_down', label: 'gpu_down', sev: 'critical' },
        { key: 'kernel_nvrm_oom', label: 'kernel_nvrm_oom', sev: 'warning' },
        { key: 'sys_memory_pressure', label: 'sys_memory_pressure', sev: 'warning' },
        { key: 'api_5xx', label: 'api_5xx', sev: 'warning' },
        { key: 'log_critical', label: 'log_critical', sev: 'critical' },
        { key: 'log_error_rate', label: 'log_error_rate', sev: 'warning' },
      ],
      channels: [
        { icon: 'Send', name: 'Email — Resend', body: 'Email HTML avec URL d\'ack à usage unique embarquée.' },
        { icon: 'MessageSquare', name: 'Telegram', body: 'Boutons inline. Ack en un tap.' },
        { icon: 'Wrench', name: 'Webhook', body: 'POST JSON générique pour votre pipeline.' },
      ],
    },
    telegram: {
      kicker: 'Telegram, bidirectionnel',
      title: 'Acquittez les alertes en un tap',
      bullets: [
        'Commandes slash — /status, /silence, /maintenance, /ack',
        'Boutons inline, confirmations, audit log',
        'Binding par utilisateur (Telegram ↔ utilisateur Zeus), scope par sévérité',
        'Rate-limit, dédupliqué, fingerprinté',
      ],
      replyA: 'Acquitté par christophe — il y a 12 secondes',
      replyB: 'gpu_mem_exhausted sur spt-node4 · GPU 0 · 23,8 GiB / 24 GiB',
    },
    backups: {
      kicker: 'Sauvegardes',
      title: 'Postgres + Neo4j à chaud, chiffrés, retenus',
      bullets: [
        'Dumps PostgreSQL via conteneurs zeus-pgdump éphémères (aiodocker)',
        'Dumps Neo4j à chaud via APOC — aucune coupure',
        'Chiffrés avec zeus_backup_encryption_key',
        'Rétention GFS (quotidien, hebdo, mensuel), upload S3 optionnel',
        'Service pgdump-warmer cache l\'image sur le manager',
        'Endpoint /run-now pour dumps manuels',
      ],
    },
    stack: {
      title: 'Construit avec',
      sub: 'Python moderne, React moderne, sans surprise.',
      groups: [
        { title: 'Backend', items: ['Python 3.12', 'FastAPI', 'SQLAlchemy 2', 'asyncpg', 'Alembic', 'structlog', 'aiodocker', 'croniter', 'Argon2'] },
        { title: 'Données', items: ['PostgreSQL 16', 'Hypertables TimescaleDB'] },
        { title: 'Frontend', items: ['React 18', 'Vite', 'TypeScript', 'shadcn/ui', 'Radix', 'Tailwind', 'TanStack Query', 'react-hook-form + zod', 'zustand', 'recharts'] },
        { title: 'Infrastructure', items: ['Docker Swarm', 'Mono-image, CLI à rôles', 'uv (Python)', 'npm (UI)'] },
      ],
    },
    cta: {
      title: 'Votre cluster, instrumenté',
      sub: "Self-host sur le Swarm que vous opérez déjà. Mono-image unique, un seul fichier stack, login avec votre clé admin.",
      button: "Accéder à l'admin",
    },
    footer: { tagline: 'Zeus — Monitoring, alerting, analyse de logs et sauvegardes unifiés pour Docker Swarm', built: 'Construit par Sponge Theory' },
  },
}

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Activity, AlertTriangle, Archive, Bell, Bug, Cpu, Layers, LayoutDashboard,
  LineChart, MessageSquare, Send, Server, ShieldCheck, Wrench,
}

function Icon({ name, className }: { name: string; className?: string }) {
  const C = ICONS[name]
  if (!C) return null
  return <C className={className} />
}

function LangToggle() {
  const { lang, setLang } = useI18n()
  return (
    <div className="inline-flex items-center rounded-md border border-border bg-card/40 p-0.5 text-xs">
      {(['en', 'fr'] as const).map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          className={cn(
            'rounded-sm px-2.5 py-1 font-medium uppercase tracking-wider transition',
            lang === l ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {l}
        </button>
      ))}
    </div>
  )
}

function SeverityDot({ sev }: { sev: 'critical' | 'warning' | 'info' }) {
  const color =
    sev === 'critical' ? 'bg-destructive shadow-[0_0_12px_rgba(239,68,68,0.6)]' :
    sev === 'warning' ? 'bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.5)]' :
    'bg-sky-400'
  return <span className={cn('inline-block h-1.5 w-1.5 rounded-full', color)} />
}

export function LandingPage() {
  const whoami = useAuth((s) => s.whoami)
  const initialized = useAuth((s) => s.initialized)
  const loading = useAuth((s) => s.loading)
  const refresh = useAuth((s) => s.refresh)
  const navigate = useNavigate()
  const { lang } = useI18n()
  const c = COPY[lang]
  const videoRef = useRef<HTMLVideoElement>(null)

  // Probe auth once; if authenticated, redirect to dashboard.
  useEffect(() => {
    if (!initialized && !loading) void refresh()
  }, [initialized, loading, refresh])

  if (initialized && whoami) {
    return <Navigate to="/dashboard" replace />
  }

  const videoSrc = lang === 'fr' ? '/zeus-demo-fr.mp4' : '/zeus-demo-en.mp4'

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* background flair */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 left-1/2 h-[800px] w-[1200px] -translate-x-1/2 rounded-full bg-primary/20 blur-3xl opacity-50" />
        <div className="absolute top-[40%] -left-32 h-[500px] w-[500px] rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="absolute top-[60%] -right-40 h-[600px] w-[600px] rounded-full bg-sky-500/10 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse 60% 50% at 50% 30%, black 0%, transparent 80%)',
          }}
        />
      </div>

      {/* nav */}
      <header className="sticky top-0 z-20 backdrop-blur bg-background/70 border-b border-border/50">
        <div className="container flex h-14 items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-primary/15 text-primary grid place-items-center font-bold text-sm">
              Z
            </div>
            <span className="text-base font-semibold tracking-tight">Zeus</span>
            <Badge variant="outline" className="ml-2 hidden sm:inline-flex border-primary/30 text-primary">
              by Sponge Theory
            </Badge>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition">{c.nav.features}</a>
            <a href="#video" className="hover:text-foreground transition">{c.nav.video}</a>
            <a href="#stack" className="hover:text-foreground transition">{c.nav.stack}</a>
          </nav>
          <div className="flex items-center gap-2">
            <LangToggle />
            <Button size="sm" onClick={() => navigate(ADMIN_URL)}>
              <LogIn className="h-4 w-4 mr-1.5" />
              {c.nav.admin}
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="container pt-16 pb-20 lg:pt-24 lg:pb-28">
        <div className="max-w-4xl mx-auto text-center">
          <Badge variant="outline" className="border-primary/40 text-primary mb-6">
            <Sparkles className="h-3 w-3 mr-1.5" />
            {c.hero.badge}
          </Badge>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.05]">
            {c.hero.headline[0]}{' '}
            <span className="text-primary">{c.hero.headline[1]}</span>
            <span className="block text-muted-foreground/80 text-3xl md:text-5xl font-semibold mt-2">
              {c.hero.headline[2]}
            </span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {c.hero.sub}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button size="lg" onClick={() => navigate(ADMIN_URL)} className="text-base px-6 h-12">
              <LogIn className="h-5 w-5 mr-2" />
              {c.hero.cta_admin}
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-base px-6 h-12"
              onClick={() => {
                document.getElementById('video')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              <Play className="h-5 w-5 mr-2" />
              {c.hero.cta_video}
            </Button>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
            {c.hero.chips.map((chip) => (
              <span
                key={chip}
                className="inline-flex items-center rounded-full border border-border/60 bg-card/40 px-3 py-1 text-xs text-muted-foreground"
              >
                {chip}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO */}
      <section id="video" className="container pb-24">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80 font-semibold">{c.video.kicker}</p>
            <h2 className="text-3xl md:text-4xl font-bold mt-3">{c.video.title}</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">{c.video.caption}</p>
          </div>
          <div className="relative rounded-xl overflow-hidden border border-border/80 bg-card/40 shadow-2xl shadow-primary/10">
            <video
              ref={videoRef}
              key={videoSrc}
              src={videoSrc}
              controls
              playsInline
              preload="metadata"
              poster="/zeus-video-poster.svg"
              className="w-full aspect-video bg-black"
            />
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section id="features" className="container pb-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{c.pillars.title}</h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">{c.pillars.sub}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {c.pillars.items.map((p) => (
            <Card key={p.title} className="border-border/60 bg-card/40 hover:border-primary/40 transition">
              <CardContent className="pt-6">
                <div className="h-10 w-10 rounded-lg bg-primary/15 text-primary grid place-items-center mb-4">
                  <Icon name={p.icon} className="h-5 w-5" />
                </div>
                <h3 className="font-semibold text-lg">{p.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{p.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="container pb-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{c.features.title}</h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">{c.features.sub}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {c.features.cards.map((f) => (
            <Card key={f.title} className="border-border/60 bg-card/40 hover:border-primary/40 transition">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 shrink-0 rounded-lg bg-primary/15 text-primary grid place-items-center">
                    <Icon name={f.icon} className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-lg">{f.title}</h3>
                      {f.tag && (
                        <Badge variant="outline" className="border-primary/30 text-primary/90 text-[10px]">
                          {f.tag}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.body}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ALERTING */}
      <section className="container pb-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80 font-semibold">{c.alerting.kicker}</p>
            <h2 className="text-3xl md:text-4xl font-bold mt-3">{c.alerting.title}</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">{c.alerting.sub}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-border/60 bg-card/40">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Rules</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 font-mono text-xs">
                  {c.alerting.rules.map((r) => (
                    <div key={r.key} className="flex items-center gap-2 px-2.5 py-1.5 rounded border border-border/40 bg-background/40">
                      <SeverityDot sev={r.sev} />
                      <span className="truncate">{r.label}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-4">+ 4 more — node_thermal_pressure_high, disk_warning, api_unreachable, health_check_auto_disabled</p>
              </CardContent>
            </Card>
            <div className="space-y-4">
              {c.alerting.channels.map((ch) => (
                <Card key={ch.name} className="border-border/60 bg-card/40">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-lg bg-primary/15 text-primary grid place-items-center">
                        <Icon name={ch.icon} className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{ch.name}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{ch.body}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TELEGRAM */}
      <section className="container pb-20">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80 font-semibold">{c.telegram.kicker}</p>
            <h2 className="text-3xl md:text-4xl font-bold mt-3">{c.telegram.title}</h2>
            <ul className="mt-6 space-y-3">
              {c.telegram.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <Card className="border-border/60 bg-card/40">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 pb-4 border-b border-border/40">
                <div className="h-8 w-8 rounded-full bg-sky-500/20 text-sky-400 grid place-items-center">
                  <MessageSquare className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-sm font-semibold">Zeus bot</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider">online</div>
                </div>
              </div>
              <div className="mt-4 space-y-3 text-sm">
                <div className="rounded-lg bg-destructive/15 border border-destructive/40 px-3 py-2.5">
                  <div className="flex items-center gap-2 text-xs font-semibold text-destructive">
                    <AlertTriangle className="h-3.5 w-3.5" /> CRITICAL
                  </div>
                  <div className="mt-1 text-foreground/90 font-mono text-xs">{c.telegram.replyB}</div>
                </div>
                <div className="flex gap-2">
                  <button className="flex-1 rounded-md border border-border/60 bg-background/60 px-3 py-2 text-xs font-medium hover:border-primary/50">
                    Ack
                  </button>
                  <button className="flex-1 rounded-md border border-border/60 bg-background/60 px-3 py-2 text-xs font-medium hover:border-primary/50">
                    Silence 1h
                  </button>
                </div>
                <div className="text-xs text-emerald-400/90 font-mono px-1">
                  ✓ {c.telegram.replyA}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* BACKUPS */}
      <section className="container pb-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80 font-semibold">{c.backups.kicker}</p>
            <h2 className="text-3xl md:text-4xl font-bold mt-3">{c.backups.title}</h2>
          </div>
          <Card className="border-border/60 bg-card/40">
            <CardContent className="pt-6">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {c.backups.bullets.map((b) => (
                  <div key={b} className="flex items-start gap-3 text-sm">
                    <Archive className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span className="text-muted-foreground">{b}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* STACK */}
      <section id="stack" className="container pb-20">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{c.stack.title}</h2>
          <p className="text-muted-foreground mt-3">{c.stack.sub}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
          {c.stack.groups.map((g) => (
            <Card key={g.title} className="border-border/60 bg-card/40">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-sm uppercase tracking-wider text-primary/90 mb-3">{g.title}</h3>
                <div className="flex flex-wrap gap-1.5">
                  {g.items.map((it) => (
                    <span key={it} className="text-xs px-2 py-0.5 rounded border border-border/50 bg-background/40 text-muted-foreground">
                      {it}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container pb-20">
        <div className="max-w-3xl mx-auto rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent p-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{c.cta.title}</h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">{c.cta.sub}</p>
          <Button
            size="lg"
            onClick={() => navigate(ADMIN_URL)}
            className="mt-6 text-base px-6 h-12"
          >
            <LogIn className="h-5 w-5 mr-2" />
            {c.cta.button}
          </Button>
        </div>
      </section>

      {/* footer */}
      <footer className="border-t border-border/50">
        <div className="container py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded bg-primary/15 text-primary grid place-items-center font-bold text-xs">Z</div>
            <span>{c.footer.tagline}</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <span className="text-xs">{c.footer.built}</span>
            <a
              href="mailto:contact@sponge-theory.ai"
              className="text-xs hover:text-foreground inline-flex items-center gap-1"
            >
              <Mail className="h-3 w-3" /> contact@sponge-theory.ai
            </a>
            <a
              href="https://sponge-theory.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs hover:text-foreground inline-flex items-center gap-1"
            >
              <Globe className="h-3 w-3" /> sponge-theory.ai
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}
